import { twMerge } from 'tailwind-merge';

import {
  FieldValue,
  FieldValues,
  FilterFormProps,
  FilterProps,
} from '@/models/FilterConfig';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import BooleanFilter from './BooleanFilter';
import ButtonFilter from './ButtonFilter';
import DualArrowOrderingFilter from './DualArrowOrderingFilter';
import MultipleBooleanFilter from './MultipleBooleanFilter';
import MultipleFilter from './MultipleFilter';
import QuickFilters from './QuickFilters';
import SearchFilter from './SearchFilter';
import SelectFilter from './SelectFilter';
import TextArrowOrderingFilter from './TextArrowOrderingFilter';
import ObjectSelectFilter from './ObjectSelectFilter';

type Props = {
  configs: FilterProps[];
  filter?: FieldValues;
  onChange(data: FieldValues): void;
  className?: Pick<ClassNameComponent, 'base' | 'ul' | 'li'>;
  hideQuick?: boolean;
};

export default function Filter(props: Props) {
  const { configs, filter, onChange, className, hideQuick } = props;

  function setValue(name: string, value: FieldValue) {
    const updated = { ...filter, [name]: value } as FieldValues;
    onChange(updated);
  }

  function setValues(object: Partial<FieldValues>) {
    onChange({ ...filter, ...object });
  }

  return (
    <section className={twMerge('flex flex-col gap-2', className?.base)}>
      <ul
        className={twMerge(
          'flex gap-1.5 items-center flex-wrap',
          className?.ul,
        )}
      >
        {configs.map((config, index) => (
          <ConditionalRenderer
            key={config.type + index}
            condition={!config.hide}
          >
            <li className={className?.li}>
              <FilterItem
                config={config}
                setValue={setValue}
                filter={filter}
                setValues={setValues}
              />
            </li>
          </ConditionalRenderer>
        ))}
      </ul>
      <ConditionalRenderer condition={!hideQuick}>
        <QuickFilters filter={filter} setValue={setValue} />
      </ConditionalRenderer>
    </section>
  );
}

type FilterItemProps = FilterFormProps & {
  config: FilterProps;
  filter?: FieldValues;
};

function FilterItem(props: FilterItemProps) {
  const { config, filter } = props;

  if (config.type === 'boolean') {
    return (
      <BooleanFilter
        {...config}
        {...props}
        value={filter?.[config.name] as boolean}
      />
    );
  }

  if (config.type === 'multiple') {
    return (
      <MultipleFilter
        {...config}
        {...props}
        values={filter?.[config.name] as Array<string | number>}
      />
    );
  }

  if (config.type === 'search') {
    return (
      <SearchFilter
        {...config}
        {...props}
        value={filter?.[config.name] as string}
      />
    );
  }

  if (config.type === 'select') {
    return (
      <SelectFilter {...config} {...props} value={filter?.[config.name]} />
    );
  }

  if (config.type === 'multipleBoolean') {
    return (
      <MultipleBooleanFilter
        {...config}
        {...props}
        values={config.options
          .filter(option => filter?.[option.name])
          .map(option => option.name)}
      />
    );
  }

  if (config.type === 'dualArrowOrdering') {
    return (
      <DualArrowOrderingFilter
        {...config}
        {...props}
        value={filter?.[config.name]}
      />
    );
  }

  if (config.type === 'textArrowOrdering') {
    return (
      <TextArrowOrderingFilter
        {...config}
        {...props}
        value={filter?.[config.name]}
      />
    );
  }

  if (config.type === 'button') {
    return (
      <ButtonFilter
        {...config}
        {...props}
        value={config.value ?? filter?.[config.name]}
        active={!!filter?.[config.name]}
      />
    );
  }

  if (config.type === 'selectComposite') {
    return <ObjectSelectFilter {...config} {...props} value={config.value} />;
  }

  return null;
}
